import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Search, Loader2, Ban, CheckCircle2, Trash2, RotateCcw, Gift,
  Coins, Shield, User as UserIcon, Tag, Plus, XCircle, AlertTriangle,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const badge = (tier) => {
  const map = {
    free: 'bg-white/10 text-white/70',
    pro: 'bg-blue-500/20 text-blue-300',
    expert: 'bg-amber-500/20 text-amber-300',
  };
  return `inline-block px-2 py-0.5 rounded text-xs uppercase font-mono ${map[tier] || map.free}`;
};

const statusBadge = (u) => {
  if (u.deleted_at) return <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-300">Supprimé</span>;
  if (u.suspended) return <span className="inline-block px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-300">Suspendu</span>;
  return <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-300">Actif</span>;
};

export default function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inactiveFilter, setInactiveFilter] = useState('all');
  const [selected, setSelected] = useState(null); // user detail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPromos, setShowPromos] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // 'soft' | 'hard' | null
  const [bulkGiftOpen, setBulkGiftOpen] = useState(false);
  const [bulkGiftTier, setBulkGiftTier] = useState('pro');
  const [bulkGiftMonths, setBulkGiftMonths] = useState(1);
  const [bulkGiftReason, setBulkGiftReason] = useState('');
  const [referredOnly, setReferredOnly] = useState(false);

  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (tierFilter !== 'all') params.tier = tierFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (inactiveFilter !== 'all') params.inactive_days = parseInt(inactiveFilter, 10);
      if (referredOnly) params.referred_only = true;
      const { data } = await axios.get(`${API}/admin/users`, { params });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [page, search, tierFilter, statusFilter, inactiveFilter, referredOnly]);

  useEffect(() => { load(); }, [load]);

  // Reset selection when the list changes (search, page, filter)
  useEffect(() => { setSelectedIds(new Set()); }, [users]);

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    // Only toggle among selectable rows (non-admin, not deleted)
    const selectable = users.filter(u => !u.is_admin);
    const allSelected = selectable.every(u => selectedIds.has(u.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map(u => u.id)));
    }
  };

  const runBulkDelete = async (mode) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const endpoint = mode === 'hard' ? '/admin/users/bulk-hard-delete' : '/admin/users/bulk-delete';
      const { data } = await axios.post(`${API}${endpoint}`, { user_ids: ids, reason: 'admin_bulk_ui' });
      const msg = mode === 'hard'
        ? `${data.deleted} compte(s) supprimé(s) définitivement`
        : `${data.deleted} compte(s) archivé(s) (purge dans 30j)`;
      toast.success(msg);
      if (data.skipped_admins?.length) {
        toast.warning(`Admins ignorés : ${data.skipped_admins.join(', ')}`);
      }
      setSelectedIds(new Set());
      setBulkConfirm(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression groupée');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkSuspend = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/bulk-suspend`, { user_ids: ids, reason: 'admin_bulk_ui' });
      toast.success(`${data.suspended} compte(s) suspendu(s)`);
      if (data.skipped_admins?.length) {
        toast.warning(`Admins ignorés : ${data.skipped_admins.join(', ')}`);
      }
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suspension groupée');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkGift = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/bulk-gift`, {
        user_ids: ids,
        tier: bulkGiftTier,
        months: bulkGiftMonths,
        reason: bulkGiftReason.trim() || 'admin_bulk_gift',
      });
      toast.success(`🎁 ${data.gifted} abonnement(s) ${bulkGiftTier.toUpperCase()} offert(s) pour ${bulkGiftMonths} mois`);
      if (data.skipped?.length) {
        toast.warning(`${data.skipped.length} user(s) ignoré(s) (introuvables ou supprimés)`);
      }
      setSelectedIds(new Set());
      setBulkGiftOpen(false);
      setBulkGiftReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur cadeau groupé');
    } finally {
      setBulkBusy(false);
    }
  };

  const openDetail = async (userId) => {
    setSelected(userId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur détail');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (selected) await openDetail(selected);
    await load();
  };

  return (
    <>
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs mb-1 block">Recherche (email/nom)</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="ex: jeremy@..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
                data-testid="admin-user-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Tier</Label>
            <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px]" data-testid="admin-tier-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Statut</Label>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px]" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="suspended">Suspendus</SelectItem>
                <SelectItem value="deleted">Supprimés</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Inactivité</Label>
            <Select value={inactiveFilter} onValueChange={(v) => { setInactiveFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="admin-inactive-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="1">Jamais / &gt; 1 jour</SelectItem>
                <SelectItem value="7">Inactifs &gt; 7 jours</SelectItem>
                <SelectItem value="30">Inactifs &gt; 30 jours</SelectItem>
                <SelectItem value="60">Inactifs &gt; 60 jours</SelectItem>
                <SelectItem value="90">Inactifs &gt; 90 jours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" onClick={() => { setPage(1); load(); }} data-testid="admin-user-search-btn">
            Rechercher
          </Button>
          <Button
            variant={referredOnly ? "default" : "outline"}
            onClick={() => { setReferredOnly(!referredOnly); setPage(1); }}
            className={referredOnly ? "bg-emerald-500 hover:bg-emerald-600 text-black" : ""}
            data-testid="admin-referred-only-filter"
            title="Filtrer uniquement les utilisateurs venus via un lien d'affiliation"
          >
            🤝 Affiliés uniquement
          </Button>
          <Button variant="outline" onClick={() => setShowPromos(true)} data-testid="admin-open-promos">
            <Tag className="w-4 h-4 mr-1" /> Codes promo
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {/* BULK ACTION BAR */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-500/10 border-b border-red-500/30" data-testid="bulk-action-bar">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="font-semibold text-red-300">{selectedIds.size} utilisateur(s) sélectionné(s)</span>
              <button
                className="text-xs text-muted-foreground hover:text-white ml-2 underline"
                onClick={() => setSelectedIds(new Set())}
                data-testid="bulk-clear-btn"
              >
                Tout désélectionner
              </button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={bulkBusy}
                onClick={() => setBulkGiftOpen(true)}
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                data-testid="bulk-gift-btn"
              >
                <Gift className="w-4 h-4 mr-1" />
                Offrir ({selectedIds.size})
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkBusy}
                onClick={runBulkSuspend}
                data-testid="bulk-suspend-btn"
              >
                <Ban className="w-4 h-4 mr-1" />
                Suspendre ({selectedIds.size})
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkBusy}
                onClick={() => setBulkConfirm('soft')}
                data-testid="bulk-soft-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Archiver ({selectedIds.size})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={bulkBusy}
                onClick={() => setBulkConfirm('hard')}
                data-testid="bulk-hard-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer définitivement ({selectedIds.size})
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="text-muted-foreground bg-white/5 border-b border-white/10">
                <tr>
                  <th className="p-3 w-10">
                    <Checkbox
                      checked={users.length > 0 && users.filter(u => !u.is_admin).every(u => selectedIds.has(u.id))}
                      onCheckedChange={toggleAll}
                      data-testid="bulk-select-all"
                      aria-label="Tout sélectionner"
                    />
                  </th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Tier</th>
                  <th className="text-left p-3">Statut</th>
                  <th className="text-right p-3">Crédits</th>
                  <th className="text-left p-3">Inscription</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr
                    key={u.id}
                    className={`border-b border-white/5 hover:bg-white/5 ${selectedIds.has(u.id) ? 'bg-red-500/5' : ''}`}
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="p-3">
                      {!u.is_admin && (
                        <Checkbox
                          checked={selectedIds.has(u.id)}
                          onCheckedChange={() => toggleId(u.id)}
                          data-testid={`bulk-select-${u.id}`}
                          aria-label={`Sélectionner ${u.email}`}
                        />
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{u.email}</div>
                      <div className="flex gap-2 flex-wrap items-center mt-0.5">
                        {u.is_admin && <span className="text-xs text-amber-400">👑 Admin</span>}
                        {u.gifted_by_admin && <span className="text-xs text-emerald-400">🎁 Cadeau</span>}
                        {u.referred_by_code && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 font-mono border border-emerald-500/20"
                            title={`Recommandé via lien d'affiliation ${u.referred_by_code}${u.referred_at ? ' le ' + u.referred_at.substring(0, 10) : ''}`}
                            data-testid={`user-referred-badge-${u.id}`}
                          >
                            🤝 via {u.referred_by_code}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3"><span className={badge(u.subscription_tier)}>{u.subscription_tier || 'free'}</span></td>
                    <td className="p-3">{statusBadge(u)}</td>
                    <td className="p-3 text-right font-mono">{u.bonus_analyses}</td>
                    <td className="p-3 text-xs text-muted-foreground">{u.created_at?.substring(0, 10)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(u.id)} data-testid={`admin-user-detail-${u.id}`}>
                        Gérer
                      </Button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Aucun utilisateur trouvé</td></tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between p-3 border-t border-white/10 text-sm">
              <div className="text-muted-foreground">{total} utilisateur{total > 1 ? 's' : ''} au total</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
                <span className="px-3 py-1">Page {page} / {Math.max(1, Math.ceil(total / limit))}</span>
                <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Suivant</Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* USER DETAIL MODAL */}
      <UserDetailModal
        open={!!selected}
        onClose={() => { setSelected(null); setDetail(null); }}
        detail={detail}
        loading={detailLoading}
        onRefresh={refreshDetail}
      />

      {/* PROMO CODES MODAL */}
      <PromoCodesModal open={showPromos} onClose={() => setShowPromos(false)} />

      {/* BULK GIFT MODAL */}
      <Dialog open={bulkGiftOpen} onOpenChange={setBulkGiftOpen}>
        <DialogContent data-testid="bulk-gift-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" />
              Offrir un abonnement à {selectedIds.size} utilisateur(s)
            </DialogTitle>
            <DialogDescription>
              Mode cadeau — sans facturation Stripe. Chaque utilisateur recevra une notification push automatique.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-1 block">Type d&apos;abonnement</Label>
              <Select value={bulkGiftTier} onValueChange={setBulkGiftTier}>
                <SelectTrigger data-testid="bulk-gift-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pro">Pro (19€/mois)</SelectItem>
                  <SelectItem value="expert">Expert (39€/mois)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs mb-1 block">Durée (mois)</Label>
              <Input
                type="number"
                min={1}
                max={24}
                value={bulkGiftMonths}
                onChange={(e) => setBulkGiftMonths(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
                data-testid="bulk-gift-months"
              />
            </div>

            <div>
              <Label className="text-xs mb-1 block">Raison (optionnel, journalisée)</Label>
              <Input
                value={bulkGiftReason}
                onChange={(e) => setBulkGiftReason(e.target.value)}
                placeholder="ex: Campagne relance fondateurs, compensation bug…"
                data-testid="bulk-gift-reason"
              />
            </div>

            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-sm">
              <div className="flex items-start gap-2">
                <Gift className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-300 mb-1">
                    {selectedIds.size} × {bulkGiftTier.toUpperCase()} pour {bulkGiftMonths} mois
                  </div>
                  <div className="text-muted-foreground text-xs">
                    Valeur cadeau : ~{(selectedIds.size * (bulkGiftTier === 'pro' ? 19 : 39) * bulkGiftMonths).toFixed(0)}€
                    · Push notification envoyée à chaque bénéficiaire
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkGiftOpen(false)}>Annuler</Button>
            <Button
              disabled={bulkBusy}
              onClick={runBulkGift}
              className="bg-emerald-500 hover:bg-emerald-600 text-black"
              data-testid="bulk-gift-confirm-btn"
            >
              {bulkBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <Gift className="w-4 h-4 mr-1" />
              Offrir à {selectedIds.size} user(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BULK DELETE CONFIRMATION */}
      <Dialog open={bulkConfirm !== null} onOpenChange={(v) => !v && setBulkConfirm(null)}>
        <DialogContent data-testid="bulk-delete-confirm-modal">          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {bulkConfirm === 'hard' ? 'Suppression définitive' : 'Archivage groupé'}
            </DialogTitle>
            <DialogDescription>
              {bulkConfirm === 'hard' ? (
                <>
                  Vous allez <b className="text-red-400">supprimer définitivement {selectedIds.size} compte(s)</b> ainsi que
                  toutes leurs données (trades, analyses, notifications, packs, etc.). <br/>
                  <span className="text-red-300">Cette action est irréversible et conforme RGPD.</span>
                </>
              ) : (
                <>
                  Vous allez <b className="text-orange-400">archiver {selectedIds.size} compte(s)</b> — ils seront bloqués
                  immédiatement mais purgés définitivement dans <b>30 jours</b>. Vous pouvez restaurer un compte pendant
                  cette période via l&apos;action « Restaurer ». Les comptes admin seront ignorés automatiquement.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirm(null)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={bulkBusy}
              onClick={() => runBulkDelete(bulkConfirm)}
              data-testid="bulk-confirm-btn"
            >
              {bulkBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {bulkConfirm === 'hard' ? 'Supprimer définitivement' : 'Confirmer archivage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== USER DETAIL MODAL ====================

function UserDetailModal({ open, onClose, detail, loading, onRefresh }) {
  const [tier, setTier] = useState('free');
  const [months, setMonths] = useState(1);
  const [creditsDelta, setCreditsDelta] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (detail?.user) {
      setTier(detail.user.subscription_tier || 'free');
      setMonths(1);
      setCreditsDelta(0);
      setConfirmDelete(false);
    }
  }, [detail]);

  if (!open) return null;

  const u = detail?.user;
  const s = detail?.stats || {};

  const action = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-user-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            {u?.email || 'Chargement...'}
          </DialogTitle>
          <DialogDescription>
            Gestion complète du compte — toutes les actions sont journalisées
          </DialogDescription>
        </DialogHeader>

        {loading || !u ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
        ) : (
          <div className="space-y-4">
            {/* Info section */}
            <div className="grid grid-cols-2 gap-3 text-sm p-3 rounded-lg bg-white/5">
              <div><span className="text-muted-foreground">ID:</span> <code className="text-xs">{u.id}</code></div>
              <div><span className="text-muted-foreground">Nom:</span> {u.name || '—'}</div>
              <div><span className="text-muted-foreground">Inscription:</span> {u.created_at?.substring(0, 10)}</div>
              <div><span className="text-muted-foreground">Statut:</span> {statusBadge(u)}</div>
              <div><span className="text-muted-foreground">Tier:</span> <span className={badge(u.subscription_tier)}>{u.subscription_tier}</span></div>
              <div><span className="text-muted-foreground">Expire:</span> {u.subscription_expires_at?.substring(0, 10) || '—'}</div>
              <div><span className="text-muted-foreground">Stripe sub:</span> {u.stripe_subscription_id ? '✅ Stripe' : (u.gifted_by_admin ? '🎁 Cadeau' : '—')}</div>
              <div><span className="text-muted-foreground">Crédits bonus:</span> <b>{u.bonus_analyses}</b></div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-sm">
              <Stat label="Trades actifs" value={s.trades_active} />
              <Stat label="Trades clos" value={s.trades_closed} />
              <Stat label="Analyses IA" value={s.analyses_total} />
              <Stat label="Packs achetés" value={s.packs_purchased} />
              <Stat label="Push subs" value={s.push_subscriptions} />
            </div>

            {/* Actions: Subscription */}
            <div className="p-3 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-2 font-medium"><Gift className="w-4 h-4 text-emerald-400" /> Offrir un abonnement (cadeau, sans Stripe)</div>
              <div className="grid grid-cols-3 gap-2">
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free (retirer)</SelectItem>
                    <SelectItem value="pro">Pro (19€/mois)</SelectItem>
                    <SelectItem value="expert">Expert (39€/mois)</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" min={1} max={24} value={months} onChange={(e) => setMonths(parseInt(e.target.value) || 1)} placeholder="Durée (mois)" disabled={tier === 'free'} />
                <Button disabled={busy} onClick={() => action(
                  () => axios.put(`${API}/admin/users/${u.id}/subscription`, { tier, months }),
                  tier === 'free' ? 'Abonnement retiré' : `${tier} offert pour ${months} mois`
                )} data-testid="admin-apply-tier-btn">
                  Appliquer
                </Button>
              </div>
            </div>

            {/* Actions: Credits */}
            <div className="p-3 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-2 font-medium"><Coins className="w-4 h-4 text-amber-400" /> Crédits bonus (analyses)</div>
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" value={creditsDelta} onChange={(e) => setCreditsDelta(parseInt(e.target.value) || 0)} placeholder="+10 ou -5" />
                <Button disabled={busy || creditsDelta === 0} onClick={() => action(
                  () => axios.post(`${API}/admin/users/${u.id}/credits`, { delta: creditsDelta }),
                  `Crédits mis à jour (${creditsDelta > 0 ? '+' : ''}${creditsDelta})`
                )} data-testid="admin-apply-credits-btn">
                  Appliquer
                </Button>
                <div className="text-sm text-muted-foreground self-center">Actuel : <b>{u.bonus_analyses}</b></div>
              </div>
            </div>

            {/* Actions: Suspension / Deletion */}
            <div className="p-3 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-2 font-medium"><Shield className="w-4 h-4 text-orange-400" /> Modération</div>
              <div className="flex flex-wrap gap-2">
                {u.suspended ? (
                  <Button variant="outline" disabled={busy} onClick={() => action(
                    () => axios.post(`${API}/admin/users/${u.id}/reactivate`),
                    'Compte réactivé'
                  )} data-testid="admin-reactivate-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Réactiver
                  </Button>
                ) : (
                  <Button variant="outline" disabled={busy || u.is_admin} onClick={() => action(
                    () => axios.post(`${API}/admin/users/${u.id}/suspend`),
                    'Compte suspendu'
                  )} data-testid="admin-suspend-btn">
                    <Ban className="w-4 h-4 mr-1" /> Suspendre
                  </Button>
                )}
                {u.deleted_at ? (
                  <Button variant="outline" disabled={busy} onClick={() => action(
                    () => axios.post(`${API}/admin/users/${u.id}/restore`),
                    'Compte restauré'
                  )} data-testid="admin-restore-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> Restaurer
                  </Button>
                ) : confirmDelete ? (
                  <>
                    <Button variant="destructive" disabled={busy || u.is_admin} onClick={() => action(
                      () => axios.delete(`${API}/admin/users/${u.id}`),
                      'Compte supprimé (purge dans 30j)'
                    )} data-testid="admin-confirm-delete-btn">
                      Confirmer la suppression
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Annuler</Button>
                  </>
                ) : (
                  <Button variant="destructive" disabled={u.is_admin} onClick={() => setConfirmDelete(true)} data-testid="admin-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer (soft-delete 30j)
                  </Button>
                )}
                {u.is_admin && <span className="text-xs text-amber-400 self-center">👑 Compte admin — modération désactivée</span>}
              </div>
              {u.deleted_at && (
                <div className="text-xs text-red-300 mt-2">
                  Supprimé le {u.deleted_at.substring(0, 10)} — purge automatique dans 30 jours
                </div>
              )}
            </div>

            {/* Audit trail */}
            {(detail.audit_trail || []).length > 0 && (
              <div className="p-3 rounded-lg border border-white/10">
                <div className="font-medium mb-2">Historique des actions admin</div>
                <div className="max-h-40 overflow-y-auto text-xs space-y-1 font-mono">
                  {detail.audit_trail.map((a, i) => (
                    <div key={i} className="text-muted-foreground">
                      {a.created_at?.substring(0, 16).replace('T', ' ')} — {a.action} par {a.admin_email}
                      {a.meta && Object.keys(a.meta).length > 0 && <span className="ml-2 text-white/40">{JSON.stringify(a.meta)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-2 rounded bg-white/5">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-bold">{value ?? 0}</div>
  </div>
);

// ==================== PROMO CODES MODAL ====================

function PromoCodesModal({ open, onClose }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: '', percent_off: 20, amount_off_eur: '', duration: 'once', duration_in_months: 3, max_redemptions: '', expires_at: '',
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setPromos(data.promo_codes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Stripe (clé configurée ?)');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    if (!form.code.trim()) return toast.error('Code requis');
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        duration: form.duration,
      };
      if (form.percent_off) payload.percent_off = Number(form.percent_off);
      if (form.amount_off_eur) payload.amount_off_eur = Number(form.amount_off_eur);
      if (form.duration === 'repeating') payload.duration_in_months = Number(form.duration_in_months);
      if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();

      await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success(`Code ${payload.code} créé sur Stripe`);
      setShowCreate(false);
      setForm({ code: '', percent_off: 20, amount_off_eur: '', duration: 'once', duration_in_months: 3, max_redemptions: '', expires_at: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Désactiver ce code promo ?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`);
      toast.success('Code désactivé');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5" /> Codes promo</DialogTitle>
          <DialogDescription>Créez et gérez les codes promo Stripe (réduction €) et les codes d&apos;essai gratuit (Expert 7j)</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="stripe" className="w-full mt-2">
          <TabsList className="grid grid-cols-2 w-full mb-3">
            <TabsTrigger value="stripe" data-testid="promo-tab-stripe">💳 Stripe (réduction)</TabsTrigger>
            <TabsTrigger value="trial" data-testid="promo-tab-trial">🎁 Essai gratuit</TabsTrigger>
          </TabsList>

          <TabsContent value="stripe">
        <div className="flex justify-end mb-2">
          <Button size="sm" onClick={() => setShowCreate(v => !v)} data-testid="admin-new-promo-btn">
            <Plus className="w-4 h-4 mr-1" /> {showCreate ? 'Annuler' : 'Nouveau code'}
          </Button>
        </div>

        {showCreate && (
          <div className="p-3 rounded-lg border border-white/10 mb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Code (auto uppercase)</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="LAUNCH50" data-testid="promo-code-input" />
              </div>
              <div>
                <Label className="text-xs">Durée</Label>
                <Select value={form.duration} onValueChange={v => setForm({ ...form, duration: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Une fois (1ère facture)</SelectItem>
                    <SelectItem value="repeating">Récurrent X mois</SelectItem>
                    <SelectItem value="forever">Pour toujours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">% de réduction (1-100)</Label>
                <Input type="number" min={1} max={100} value={form.percent_off} onChange={e => setForm({ ...form, percent_off: e.target.value, amount_off_eur: '' })} />
              </div>
              <div>
                <Label className="text-xs">OU montant fixe (€)</Label>
                <Input type="number" step="0.5" value={form.amount_off_eur} onChange={e => setForm({ ...form, amount_off_eur: e.target.value, percent_off: '' })} />
              </div>
              {form.duration === 'repeating' && (
                <div>
                  <Label className="text-xs">Nombre de mois</Label>
                  <Input type="number" min={1} value={form.duration_in_months} onChange={e => setForm({ ...form, duration_in_months: e.target.value })} />
                </div>
              )}
              <div>
                <Label className="text-xs">Max utilisations (optionnel)</Label>
                <Input type="number" min={1} value={form.max_redemptions} onChange={e => setForm({ ...form, max_redemptions: e.target.value })} placeholder="illimité" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Expire le (optionnel)</Label>
                <Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
              </div>
            </div>
            <Button disabled={busy} onClick={create} className="w-full" data-testid="admin-create-promo-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Créer sur Stripe
            </Button>
          </div>
        )}

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-muted-foreground border-b border-white/10">
              <tr>
                <th className="text-left p-2">Code</th>
                <th className="text-left p-2">Réduction</th>
                <th className="text-left p-2">Durée</th>
                <th className="text-right p-2">Utilisé</th>
                <th className="text-left p-2">Statut</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {promos.map(p => (
                <tr key={p.id} className="border-b border-white/5" data-testid={`promo-row-${p.code}`}>
                  <td className="p-2 font-mono font-bold">{p.code}</td>
                  <td className="p-2">
                    {p.coupon.percent_off ? `${p.coupon.percent_off}%` : `${p.coupon.amount_off?.toFixed(2)} €`}
                  </td>
                  <td className="p-2 text-xs">
                    {p.coupon.duration === 'once' && '1x'}
                    {p.coupon.duration === 'repeating' && `${p.coupon.duration_in_months} mois`}
                    {p.coupon.duration === 'forever' && 'Toujours'}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ''}
                  </td>
                  <td className="p-2">
                    {p.active ? <span className="text-emerald-400 text-xs">Actif</span> : <span className="text-red-400 text-xs">Désactivé</span>}
                  </td>
                  <td className="p-2 text-right">
                    {p.active && (
                      <Button size="sm" variant="ghost" onClick={() => deactivate(p.id)}>
                        <XCircle className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {promos.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucun code promo</td></tr>
              )}
            </tbody>
          </table>
        )}
          </TabsContent>

          <TabsContent value="trial">
            <TrialPromoCodesTab />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Trial Promo Codes (free Expert trial via signup) ============
function TrialPromoCodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: '',
    duration_days: 7,
    benefit_tier: 'expert',
    max_uses: '',
    expires_at: '',
    note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/trial-promo-codes`);
      setCodes(data.trial_promo_codes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code.trim()) return toast.error('Code requis');
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        duration_days: parseInt(form.duration_days, 10),
        benefit_tier: form.benefit_tier,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        note: form.note || null,
      };
      await axios.post(`${API}/admin/trial-promo-codes`, payload);
      toast.success(`Code ${payload.code} cree - ${payload.duration_days} jours ${payload.benefit_tier} offerts`);
      setForm({ code: '', duration_days: 7, benefit_tier: 'expert', max_uses: '', expires_at: '', note: '' });
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (id, code) => {
    if (!window.confirm(`Desactiver le code ${code} ? Les inscrits existants gardent leur essai.`)) return;
    try {
      await axios.delete(`${API}/admin/trial-promo-codes/${id}`);
      toast.success('Code desactive');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground p-2 rounded bg-emerald-500/5 border border-emerald-500/10">
        Ces codes offrent un <strong>essai gratuit d abonnement</strong> (ex: 7 jours Expert) a l inscription. L utilisateur est auto-downgrade en free a la fin de la periode, sauf s il a souscrit a Stripe entre-temps.
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowCreate(v => !v)} data-testid="admin-new-trial-btn">
          <Plus className="w-4 h-4 mr-1" /> {showCreate ? 'Annuler' : 'Nouveau code essai'}
        </Button>
      </div>

      {showCreate && (
        <div className="p-3 rounded-lg border border-white/10 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Code (auto uppercase)</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH7" data-testid="trial-code-input" />
            </div>
            <div>
              <Label className="text-xs">Duree (jours)</Label>
              <Input type="number" min={1} max={365} value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} data-testid="trial-duration-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tier offert</Label>
              <Select value={form.benefit_tier} onValueChange={v => setForm({ ...form, benefit_tier: v })}>
                <SelectTrigger data-testid="trial-tier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expert">Expert</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Max utilisations (vide = illimite)</Label>
              <Input type="number" min={1} value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="Illimite" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date d expiration (vide = jamais)</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Note interne (optionnel)</Label>
              <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Ex: Campagne email fev 2026" />
            </div>
          </div>
          <Button disabled={busy} onClick={create} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="admin-create-trial-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Creer le code d essai
          </Button>
        </div>
      )}

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-muted-foreground border-b border-white/10">
            <tr>
              <th className="text-left p-2">Code</th>
              <th className="text-left p-2">Tier</th>
              <th className="text-right p-2">Duree</th>
              <th className="text-right p-2">Utilise</th>
              <th className="text-left p-2">Expire</th>
              <th className="text-left p-2">Statut</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`trial-row-${c.code}`}>
                <td className="p-2 font-mono font-bold text-emerald-400">{c.code}</td>
                <td className="p-2 text-xs uppercase">{c.benefit_tier}</td>
                <td className="p-2 text-right font-mono">{c.duration_days}j</td>
                <td className="p-2 text-right font-mono">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td className="p-2 text-xs text-muted-foreground">{c.expires_at ? c.expires_at.substring(0, 10) : 'Jamais'}</td>
                <td className="p-2">
                  {c.active
                    ? <span className="text-emerald-400 text-xs">Actif</span>
                    : <span className="text-red-400 text-xs">Desactive</span>}
                </td>
                <td className="p-2 text-right">
                  {c.active && (
                    <Button size="sm" variant="ghost" onClick={() => deactivate(c.id, c.code)} title="Desactiver">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {codes.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Aucun code d essai - creez en un pour demarrer votre recrutement</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

